import React, { useState, useEffect } from 'react';
import { billingDateService } from '../services/billingDateService';

interface InvoiceLineBillingDateEditorProps {
  invoiceLineId: string;
  initialStartDate?: string;
  initialEndDate?: string;
  billingCycle?: string;
  invoiceLineName?: string;
  onSave?: (newEndDate: string) => void;
  onCancel?: () => void;
  onError?: (error: string) => void;
  readOnly?: boolean;
}

const InvoiceLineBillingDateEditor: React.FC<InvoiceLineBillingDateEditorProps> = ({
  invoiceLineId,
  initialStartDate,
  initialEndDate,
  billingCycle,
  invoiceLineName,
  onSave,
  onCancel,
  onError,
  readOnly = false
}) => {
  const [startDate, setStartDate] = useState(initialStartDate || '');
  const [endDate, setEndDate] = useState(initialEndDate || '');
  const [cycle, setCycle] = useState(billingCycle || '1_month');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState<{
    isValid: boolean;
    expectedEndDate: string;
    actualDifference: number;
    message: string;
  } | null>(null);

  // Load invoice line data if not provided
  useEffect(() => {
    if (!initialStartDate || !initialEndDate) {
      loadInvoiceLineData();
    }
  }, [invoiceLineId]);

  // Validate whenever dates or cycle change
  useEffect(() => {
    if (startDate && endDate && cycle) {
      const validationResult = billingDateService.validateBillingPeriod(startDate, endDate, cycle);
      setValidation(validationResult);
    }
  }, [startDate, endDate, cycle]);

  const loadInvoiceLineData = async () => {
    try {
      setLoading(true);
      const lineData = await billingDateService.getInvoiceLine(invoiceLineId);
      setStartDate(lineData.line_billing_start.split('T')[0]);
      setEndDate(lineData.line_billing_end.split('T')[0]);
      setCycle(lineData.billing_cycle);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load invoice line data';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (validation && !validation.isValid && validation.actualDifference !== 0) {
      const confirmMessage = `The billing period is ${Math.abs(validation.actualDifference)} day(s) ${
        validation.actualDifference > 0 ? 'too long' : 'too short'
      }. Are you sure you want to save this date?`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      setSaving(true);
      await billingDateService.updateInvoiceLineBillingDate(
        invoiceLineId,
        startDate || undefined,
        endDate,
        'billing_date_editor'
      );
      onSave?.(endDate);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update billing date';
      onError?.(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleUseExpectedDate = () => {
    if (validation?.expectedEndDate) {
      setEndDate(validation.expectedEndDate);
    }
  };

  const handleRecalculate = () => {
    if (startDate && cycle) {
      const expectedDate = billingDateService.calculateExpectedEndDate(startDate, cycle);
      setEndDate(expectedDate);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-line-billing-date-editor">
      <div className="card">
        <div className="card-header">
          <h5>Edit Billing Period</h5>
          {invoiceLineName && (
            <small className="text-muted">Line: {invoiceLineName}</small>
          )}
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">Billing Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={readOnly}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Billing End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={readOnly}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Billing Cycle</label>
              <select
                className="form-select"
                value={cycle}
                onChange={(e) => setCycle(e.target.value)}
                disabled={readOnly}
              >
                <option value="1_month">1 Month</option>
                <option value="3_months">3 Months</option>
                <option value="6_months">6 Months</option>
                <option value="12_months">12 Months</option>
              </select>
            </div>
          </div>

          {validation && (
            <div className={`alert ${validation.isValid ? 'alert-success' : 'alert-warning'}`}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Validation:</strong> {validation.message}
                  {!validation.isValid && (
                    <div className="mt-1">
                      <small>Expected end date: {billingDateService.formatDate(validation.expectedEndDate)}</small>
                    </div>
                  )}
                </div>
                {!validation.isValid && !readOnly && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleUseExpectedDate}
                  >
                    Use Expected Date
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="row mb-3">
            <div className="col-md-6">
              <div className="card bg-light">
                <div className="card-body">
                  <h6>Current Period</h6>
                  <p className="mb-1">
                    <strong>Start:</strong> {billingDateService.formatDate(startDate)}
                  </p>
                  <p className="mb-1">
                    <strong>End:</strong> {billingDateService.formatDate(endDate)}
                  </p>
                  <p className="mb-0">
                    <strong>Duration:</strong> {
                      startDate && endDate ? 
                      Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1 : 
                      0
                    } days
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card bg-light">
                <div className="card-body">
                  <h6>Expected Period</h6>
                  {validation && (
                    <>
                      <p className="mb-1">
                        <strong>Start:</strong> {billingDateService.formatDate(startDate)}
                      </p>
                      <p className="mb-1">
                        <strong>End:</strong> {billingDateService.formatDate(validation.expectedEndDate)}
                      </p>
                      <p className="mb-0">
                        <strong>Duration:</strong> {
                          Math.ceil((new Date(validation.expectedEndDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
                        } days
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {!readOnly && (
            <div className="d-flex justify-content-between">
              <div>
                <button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={handleRecalculate}
                >
                  Recalculate End Date
                </button>
              </div>
              <div>
                {onCancel && (
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving || !startDate || !endDate}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceLineBillingDateEditor;